import { Fragment, ReactNode, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import useModalZIndexStore from '@/store/modal-zindex';

import XIcon from '@/assets/icons/x.svg';

type ModalSidebarProps = {
  leftside?: ReactNode;
  rightside?: ReactNode;
  title: string;
  modalOpen: boolean;
  setModalClose?: Function;
  position?: string;
  children?: ReactNode;
  modalId?: string;
};

const SidebarModal = ({
  leftside,
  rightside,
  title,
  modalOpen,
  setModalClose,
  position = 'right',
  children,
  modalId,
}: ModalSidebarProps) => {
  const router = useRouter();
  const id = modalId || (title ? title.toLowerCase().replace(/\s+/g, '-') : 'sidebar-modal');
  const zIndex = useModalZIndexStore((state) => state.zIndices[id] || 1005);
  const bringToFront = useModalZIndexStore((state) => state.bringToFront);

  useEffect(() => {
    if (modalOpen) {
      bringToFront(id);
    }
  }, [modalOpen, id, bringToFront]);

  return (
    <Transition.Root show={modalOpen} as={Fragment}>
      <div
        className={clsx(
          'pointer-events-none mt-20 fixed inset-y-0 flex space-y-0 rounded-lg',
          position === 'left' ? 'left-24 w-[calc(100%-4rem)]' : 'right-0 w-full',
        )}
        style={{ zIndex }}
      >
        <Transition.Child
          as={Fragment}
          enter="transform transition-opacity duration-200 sm:duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transform transition-opacity duration-200 sm:duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div 
            className="pointer-events-auto w-screen fixed top-0 bottom-0 md:relative sidebar-menu"
            onMouseDown={() => bringToFront(id)}
          >
            <div className="flex h-[calc(100%-100px)] flex-col bg-white py-6 shadow-xl dark:bg-gray-900 dark:shadow-gray-800 rounded-lg">
              <div className="px-4 sm:px-6 flex justify-between items-center bg-white dark:bg-gray-900">
                <h2 className="text-sm font-medium leading-6 text-gray-900 dark:text-white">
                  {title}
                </h2>
                <button
                  type="button"
                  className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white rounded-lg p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors inline-flex items-center cursor-pointer"
                  onClick={() => {
                    if (setModalClose) {
                      setModalClose();
                    } else {
                      const currentBasePath = router.asPath.split('#')[0].split('?')[0] || '/dashboard';
                      router.push(currentBasePath, undefined, { shallow: true });
                    }
                  }}
                  aria-label="Close modal"
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="relative mt-6 flex-1">{leftside}</div>
            </div>
          </div>
        </Transition.Child>

        <Transition.Child
          as={Fragment}
          enter="transform transition-opacity duration-200 sm:duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transform transition-opacity duration-200 sm:duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          {rightside ? (
            <div className="pointer-events-auto relative w-screen max-w-full overflow-y-scroll h-full bg-slate-100 dark:bg-gray-800">
              <div className="flex flex-col py-6 h-auto bg-slate-100 dark:bg-gray-800">
                <div className="px-4 sm:px-6">{rightside}</div>
              </div>
            </div>
          ) : (
            <div />
          )}
        </Transition.Child>
      </div>
    </Transition.Root>
  );
};

export default SidebarModal;
